import React, { useState, ChangeEvent, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import DefaultImage from "../../image/upload.png";
import { UploadedFile } from "../../types";
import { useFileUploader } from "../../utils";
import UploadFileSkeleton from "../Skeletons/UploadFileSkeleton";

interface UploadFileProps {
  setFile: (fileUrl: string) => void;
  setLogo: (logo: UploadedFile | null) => void;
  defaultImage?: string;
}

const UploadFile: React.FC<UploadFileProps> = ({
  setFile,
  setLogo,
  defaultImage,
}) => {
  const { uploadFile } = useFileUploader();
  const [uploadedImage, setUploadedImage] = useState<string>(
    defaultImage || DefaultImage
  );
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setUploadedImage(defaultImage || DefaultImage);
  }, [defaultImage]);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      if (!selectedFile.type.startsWith("image/")) {
        alert("Invalid file! Please upload an image.");
        return;
      }

      const fileUrl = URL.createObjectURL(selectedFile);
      setUploadedImage(fileUrl);
      setIsImageUploaded(true);
      setIsLoading(true);

      uploadFile(selectedFile, (result, error) => {
        setIsLoading(false);

        if (error) {
          console.error("Error uploading file:", error);
          alert("Failed to upload image. Please try again.");
        } else if (result) {
          setLogo({
            name: selectedFile.name,
            type: selectedFile.type,
            externalId: result.externalId,
            localId: "",
          });
          setFile(result.getURL);
        }
      });
    }
  };

  const handleDelete = () => {
    setUploadedImage(DefaultImage);
    setFile(DefaultImage);
    setLogo(null);
    setIsImageUploaded(false);
  };

  return (
    <div className="flex justify-between">
      {isLoading ? (
        <UploadFileSkeleton />
      ) : (
        <section className="shadow-xl container w-fit items-start border border-gray1 border-dashed rounded-lg">
          <label htmlFor="upload" className="cursor-pointer">
            <input
              type="file"
              onChange={handleChange}
              id="upload"
              className="hidden"
            />
            <img src={uploadedImage} alt="logo" className="p-2 w-44 h-40" />
          </label>
        </section>
      )}

      {isImageUploaded && !isLoading && (
        <button
          type="button"
          onClick={handleDelete}
          className="absolute bg-slate-200 bg-opacity-90 text-white w-6 h-6 rounded-lg flex items-center justify-center border-slate-200 border hover:border-slate-500"
        >
          <XMarkIcon className="h-6 w-6 text-slate-500" />
        </button>
      )}
    </div>
  );
};

export default UploadFile;
